import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $convertToMarkdownString,
  $convertFromMarkdownString,
  ORDERED_LIST,
  UNORDERED_LIST,
  LINK,
  INLINE_CODE,
  CODE,
} from '@lexical/markdown';
import type { Transformer } from '@lexical/markdown';
import { ListNode, ListItemNode, $isListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import {
  $getRoot,
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  KEY_ENTER_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from 'lexical';
import { $isCodeNode } from '@lexical/code';
import type { LexicalEditor as LexicalEditorType } from 'lexical';

import type { RichTextInputProps, RichTextInputHandle } from './RichTextInput';

const CHAT_TRANSFORMERS: Transformer[] = [ORDERED_LIST, UNORDERED_LIST, LINK, INLINE_CODE, CODE];

// Holds a ref to the underlying Lexical editor instance so the outer
// imperative handle can call clear() / focus() without exposing Lexical types.
function EditorRefPlugin({ editorRef }: { editorRef: React.MutableRefObject<LexicalEditorType | null> }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef.current = editor;

    return () => {
      editorRef.current = null;
    };
  }, [editor, editorRef]);

  return null;
}

function SubmitPlugin({ onSubmit }: { onSubmit?: (markdown: string) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent) => {
        if (event.shiftKey) return false;
        // Let Lexical handle Enter inside a list (new list item) or code block (new line).
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = selection.anchor.getNode();
          if ($isListItemNode(node) || $isListItemNode(node.getParent())) return false;
          if ($isCodeNode(node) || $isCodeNode(node.getParent())) return false;
        }
        event.preventDefault();
        editor.read(() => {
          onSubmit?.($convertToMarkdownString(CHAT_TRANSFORMERS));
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, onSubmit]);

  return null;
}

interface LexicalEditorProps extends RichTextInputProps {
  forwardedRef?: React.Ref<RichTextInputHandle>;
}

export function LexicalEditor({
  defaultValue,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  className,
  autoFocus,
  forwardedRef,
}: LexicalEditorProps) {
  const editorRef = useRef<LexicalEditorType | null>(null);

  useImperativeHandle(forwardedRef, () => ({
    clear() {
      editorRef.current?.update(() => {
        const root = $getRoot();
        root.clear();
        root.append($createParagraphNode());
      });
    },
    focus() {
      editorRef.current?.focus();
    },
  }));

  const initialConfig = {
    namespace: 'RichTextInput',
    nodes: [ListNode, ListItemNode, LinkNode, AutoLinkNode, CodeNode, CodeHighlightNode],
    editable: !disabled,
    editorState: defaultValue ? () => $convertFromMarkdownString(defaultValue, CHAT_TRANSFORMERS) : undefined,
    onError: (error: Error) => console.error('[RichTextInput]', error),
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={className}>
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
                className="outline-none w-full max-h-[7.5rem] overflow-y-auto [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-0 [&_ul]:my-0 [&_ol]:my-0 [&_pre]:my-0 [&_blockquote]:my-0"
              />
            }
            placeholder={
              placeholder ? (
                <div className="pointer-events-none absolute top-0 left-0 select-none text-muted-foreground">
                  {placeholder}
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
      </div>
      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <MarkdownShortcutPlugin transformers={CHAT_TRANSFORMERS} />
      {onChange && (
        <OnChangePlugin
          onChange={(editorState) => {
            editorState.read(() => {
              onChange($convertToMarkdownString(CHAT_TRANSFORMERS));
            });
          }}
        />
      )}
      <SubmitPlugin onSubmit={onSubmit} />
      <EditorRefPlugin editorRef={editorRef} />
    </LexicalComposer>
  );
}
